// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import Portal from 'components/atoms/Portal';
import { IconDropDown } from 'components/atoms/icons/IconDropDown';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import Calendar, { Value } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useTranslation } from 'react-i18next';
import { getFormatDate } from 'ts/formatDate';
import { mergeClasses } from 'util/commons';
import useOutsideClick from 'util/hooks/useClickOutside';
import { useGetBoundingClientRect } from 'util/hooks/useGetBoundingClientRect';
import './Calendar.scss';
import styles from './index.module.scss';
import TableFilterExpandedValue from 'components/atoms/TableFilterExpandedValue';
import { DefaultTFuncReturn } from 'i18next';
import { toast } from 'react-toastify';
import { DATE_FORMAT } from 'util/ConstantValues';

type ValueParser<IsSingle extends boolean> = IsSingle extends true
  ? string
  : [string, string];

type TableFilterCalendarProps<IsSingle extends boolean = true> = {
  isSingle?: IsSingle;
  label?: string | DefaultTFuncReturn;
  value?: ValueParser<IsSingle>;
  onChange?: (value: ValueParser<IsSingle> | undefined) => void;
  allowClear?: boolean;
  disabled?: boolean;
  expanded?:
    | boolean
    | {
        placeholder?: string | DefaultTFuncReturn;
        icon?: React.ReactNode;
        className?: string;
      };
  align?: 'left' | 'center' | 'right';
  fullWidth?: boolean;
  calendarWrapperStyle?: React.CSSProperties;
  wrapperClassName?: string;
  containerClassName?: string;
  portalPositionPoint?: (
    ref: HTMLElement | null
  ) => HTMLElement | null | undefined;
  chosenDatesLimit?: {
    limit: number;
    required?: boolean;
  };
  endDateRequired?: boolean;
  format?: string;
  fontSizeType?: 'medium' | 'large';
  darkMode?: boolean;
};

function TableFilterCalendar<IsSingle extends boolean = false>({
  isSingle,
  label,
  value,
  onChange,
  allowClear,
  disabled,
  expanded,
  align = 'left',
  fullWidth,
  calendarWrapperStyle,
  wrapperClassName,
  containerClassName,
  portalPositionPoint,
  chosenDatesLimit,
  endDateRequired,
  format = DATE_FORMAT.full_date_dash,
  fontSizeType,
  darkMode = true,
}: TableFilterCalendarProps<IsSingle>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [lastSelected, setLastSelected] = useState<string>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayValue, setDisplayValue] = useState(value);

  const handleChange = (value: ValueParser<IsSingle> | undefined) => {
    setDisplayValue(value);

    if (isSingle) {
      onChange?.(value);
      return;
    }

    if (!endDateRequired || (value?.[0] && value?.[1]) || !value) {
      onChange?.(value);
    }
  };

  useEffect(() => {
    setDisplayValue(value);
  }, [open, value]);

  const { ref: dropdownRef } = useOutsideClick({
    onClickOutside: () => {
      setOpen(false);
    },
    exceptRefs: [containerRef],
  });

  const { domRect } = useGetBoundingClientRect(
    {
      el: portalPositionPoint
        ? portalPositionPoint(containerRef.current)
        : containerRef.current?.parentElement,
    },
    [open]
  );

  useEffect(() => {
    if (!open) {
      setLastSelected(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (isSingle) {
      return;
    }

    if (chosenDatesLimit?.required && !open) {
      const rangeValue = displayValue as [string, string];

      if (rangeValue?.[0] && !rangeValue[1]) {
        const endDate = dayjs(rangeValue[0]).add(
          chosenDatesLimit.limit,
          'days'
        );

        handleChange?.([
          rangeValue[0],
          endDate.isAfter(dayjs(), 'D')
            ? dayjs().format(format)
            : endDate.format(format),
        ] as ValueParser<IsSingle>);
      } else if (rangeValue?.[1] && !rangeValue?.[0]) {
        handleChange?.([
          dayjs(rangeValue[1])
            .subtract(chosenDatesLimit.limit, 'days')
            .format(format),
          rangeValue[1],
        ] as ValueParser<IsSingle>);
      }
    }
  }, [open, chosenDatesLimit, isSingle]);

  if (disabled) {
    return <>{label}</>;
  }

  const changeDate = (calendarValue: Value | null) => {
    if (!calendarValue) {
      handleChange?.(undefined);
      return;
    }

    // Convert Calendar value to date strings
    let selectedDate: [string, string];

    if (Array.isArray(calendarValue)) {
      // Range selection
      selectedDate = [
        calendarValue[0] ? dayjs(calendarValue[0]).format(format) : '',
        calendarValue[1] ? dayjs(calendarValue[1]).format(format) : '',
      ];
    } else {
      // Single date selection
      const dateStr = dayjs(calendarValue).format(format);
      selectedDate = [dateStr, ''];
    }

    if (isSingle) {
      handleChange?.(selectedDate[0] as ValueParser<IsSingle>);
      setOpen(false);
      return;
    }

    if (chosenDatesLimit && lastSelected) {
      const [startDate, endDate] = selectedDate;

      if (startDate && endDate) {
        const diff = dayjs(endDate).diff(startDate, 'days');

        if (diff > chosenDatesLimit.limit) {
          toast.info(
            t('common.alert.date_limit_exceeded', {
              value: chosenDatesLimit.limit,
            })
          );

          if (dayjs(startDate).isSame(dayjs(lastSelected), 'days')) {
            handleChange?.([
              startDate,
              dayjs(startDate)
                .add(chosenDatesLimit.limit, 'days')
                .format(format),
            ] as ValueParser<IsSingle>);
          } else {
            handleChange?.([
              dayjs(endDate)
                .subtract(chosenDatesLimit.limit, 'days')
                .format(format),
              endDate,
            ] as ValueParser<IsSingle>);
          }
          setOpen(false);

          return;
        }
      }
    }

    if (!selectedDate[1]) {
      setLastSelected(selectedDate[0]);
    } else {
      setLastSelected(selectedDate[1]);
    }

    handleChange?.(selectedDate as ValueParser<IsSingle>);
    if (selectedDate[0] && selectedDate[1]) {
      setOpen(false);
    }
  };

  const renderActionButton = () => {
    if (expanded) {
      // Helper function to check if value exists and has content
      const hasValue = () => {
        if (!displayValue) return false;

        if (isSingle) {
          return Boolean(displayValue as string);
        } else {
          const rangeValue = displayValue as [string, string];
          return Boolean(rangeValue?.length && rangeValue.some(Boolean));
        }
      };

      // Helper function to format display value
      const getDisplayText = () => {
        if (!displayValue) return '';

        if (isSingle) {
          const singleValue = displayValue as string;

          return singleValue ? getFormatDate(singleValue, format) : '';
        } else {
          const rangeValue = displayValue as [string, string];

          return `${
            rangeValue?.[0] ? getFormatDate(rangeValue[0], format) : ''
          } - ${rangeValue?.[1] ? getFormatDate(rangeValue[1], format) : ''}`;
        }
      };

      return (
        <TableFilterExpandedValue
          value={getDisplayText()}
          valueCondition={hasValue()}
          clearCondition={hasValue()}
          allowClear={allowClear}
          onChange={handleChange}
          {...(expanded !== true && { ...expanded })}
        />
      );
    }

    return <IconDropDown color={value ? '#86EA5C' : '#B7B7B7'} />;
  };

  const renderDisplayValue = () => {
    if (expanded) {
      return null;
    }

    // Handle both single and range display values
    if (isSingle) {
      const singleValue = displayValue as string;
      if (!singleValue) return null;

      return (
        <div className={styles.displayValue}>
          <span className={styles.selectedDate}>
            {getFormatDate(singleValue, format)}
          </span>
        </div>
      );
    } else {
      const rangeValue = displayValue as [string, string];
      if (!rangeValue?.[0] && !rangeValue?.[1]) {
        return null;
      }

      return (
        <div className={styles.displayValue}>
          <span className={styles.selectedDate}>
            {rangeValue?.[0] ? getFormatDate(rangeValue[0], format) : ''} -{' '}
            {rangeValue?.[1] && getFormatDate(rangeValue[1], format)}
          </span>
        </div>
      );
    }
  };

  return (
    <div ref={containerRef} className={wrapperClassName}>
      <div
        onClick={() => setOpen(!open)}
        className={mergeClasses(styles.labelContainer, containerClassName, {
          [styles.labelExpanded]: expanded,
        })}
      >
        {label && <span className={styles.label}>{label}</span>}
        {renderActionButton()}
      </div>
      {open && domRect && (
        <Portal>
          <div
            ref={dropdownRef}
            className={mergeClasses(styles.dropdown, 'calendar-tablet', {
              [styles.dropdownCentered]: align === 'center',
              [styles.dropdownRight]: align === 'right',
            })}
            style={{
              top: domRect.top + domRect.height + 4,
              left: align === 'right' ? undefined : domRect.left,
              right:
                align === 'right'
                  ? window.innerWidth - domRect.right
                  : undefined,
              ...calendarWrapperStyle,
              ...(fullWidth && { width: domRect.width }),
            }}
          >
            {renderDisplayValue()}

            <Calendar
              value={(() => {
                if (!displayValue) return null;

                if (isSingle) {
                  const singleValue = displayValue as string;
                  return singleValue ? dayjs(singleValue).toDate() : null;
                } else {
                  const rangeValue = displayValue as [string, string];
                  return [
                    rangeValue?.[0] ? dayjs(rangeValue[0]).toDate() : null,
                    rangeValue?.[1] ? dayjs(rangeValue[1]).toDate() : null,
                  ];
                }
              })()}
              calendarType="US"
              locale={localStorage.getItem('i18nextLng') || 'ja'}
              prev2Label={null}
              next2Label={null}
              prevLabel="◀"
              nextLabel="▶"
              maxDate={new Date()}
              formatDay={(_, date: string) => getFormatDate(date, 'D')}
              onChange={changeDate}
              className={mergeClasses('react-calendar-tablet', {
                'full-width': fullWidth,
                fontSizeLarge: fontSizeType === 'large',
                light: !darkMode,
              })}
              allowPartialRange={!isSingle}
              selectRange={!isSingle}
            />
            {!expanded &&
              allowClear &&
              (() => {
                if (isSingle) {
                  return Boolean(displayValue as string);
                } else {
                  const rangeValue = displayValue as [string, string];
                  return Boolean(
                    rangeValue?.length && rangeValue.some(Boolean)
                  );
                }
              })() && (
                <div
                  onClick={(e) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    handleChange?.(undefined);
                  }}
                  className={styles.clear}
                >
                  {t('common.button.clear')}
                </div>
              )}
          </div>
        </Portal>
      )}
    </div>
  );
}

TableFilterCalendar.defaultProps = {
  allowClear: true,
};

export default TableFilterCalendar;
